import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { TestAttemptStatusSchema } from '../test-attempt-status/test-attempt-status';
import { TestQuestionSchema } from '../test-question/test-question';

/** One question's answer, as submitted and as stored. */
export const TestAttemptAnswerSchema = z
  .object({
    questionId: UuidSchema,
    /**
     * Empty means "left blank", which is a legitimate submission and scores
     * zero for that question. It is **not** the same as the question being
     * absent from the payload — but both are scored identically, so the
     * distinction never becomes a way to tell one from the other.
     */
    optionIds: z.array(UuidSchema).max(20),
  })
  .strict();

export type TestAttemptAnswer = z.infer<typeof TestAttemptAnswerSchema>;

export const TestAttemptAnswersSchema = z.array(TestAttemptAnswerSchema).max(200);

/**
 * One take of a test, as **staff** see it in the attempts viewer.
 *
 * Carries the member, because the whole point of the viewer is answering "why
 * has this member not got their discount?" — and never the answers themselves,
 * which would turn a support screen into an answer key.
 */
export const TestAttemptSchema = z
  .object({
    id: UuidSchema,
    testId: UuidSchema,
    userId: UuidSchema,
    status: TestAttemptStatusSchema,
    /** `null` until the attempt is scored; a whole percent, rounded half-up. */
    scorePct: z.number().int().min(0).max(100).nullable(),
    startedAt: z.iso.datetime(),
    /** When the TTL sweep will expire it if it is still open (matrix row 8). */
    expiresAt: z.iso.datetime(),
    submittedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .strict();

export type TestAttempt = z.infer<typeof TestAttemptSchema>;

/**
 * The attempt as the **member** sees it while sitting the test.
 *
 * The questions are embedded so resuming restores the exact paper (matrix row 2
 * for lessons, and the same promise for an attempt): the option order is the
 * stored `sortOrder`, not a per-request shuffle, or a resumed attempt would
 * renumber the answers already chosen.
 *
 * `answers` is what the server has recorded so far, so a member who resumes on a
 * different device does not start from a blank page.
 */
export const TestAttemptViewSchema = z
  .object({
    id: UuidSchema,
    testId: UuidSchema,
    status: TestAttemptStatusSchema,
    startedAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    passingScorePct: z.number().int().min(1).max(100),
    questions: z.array(TestQuestionSchema),
    answers: TestAttemptAnswersSchema,
  })
  .strict();

export type TestAttemptView = z.infer<typeof TestAttemptViewSchema>;
